import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button, Calendar, Icon, Input } from "@elaniintc/goes-design-system";
import { useState, useEffect } from "react";
import {
  type MaskitoDateParams,
  maskitoDateOptionsGenerator,
  maskitoStringifyDate,
  maskitoParseDate,
} from "@maskito/kit";
import { useMaskito } from "@maskito/react";

const params: MaskitoDateParams = {
  mode: "dd/mm/yyyy",
  separator: "/",
};
const options = maskitoDateOptionsGenerator(params);

const MAX_YEAR_OFFSET = 20; // Maximum year offset for date validation

interface Props {
  defaultDate: string;
  onChangeDate: (date: string) => void;
  className?: string;
  value?: string;
  shouldDisabledFutureDates?: boolean;
  group?:
    | "down-left"
    | "down"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left";
}

const ComparativeValueInputCalendar = ({
  onChangeDate,
  group = "down",
  className,
  defaultDate,
  shouldDisabledFutureDates,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(() => {
    if (defaultDate) {
      const parsedDate = new Date(defaultDate);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    return undefined;
  });
  const [dateValue, setDateValue] = useState<string>(() => {
    if (date) {
      return maskitoStringifyDate(date, {
        separator: "/",
        mode: "dd/mm/yyyy",
      });
    }
    return "";
  });

  const maskedInputRef = useMaskito({ options });

  // sync the defaultDate prop when it changes from outside
  useEffect(() => {
    if (!defaultDate || defaultDate === "") {
      // If the defaultDate is empty, clear the internal state
      setDate(undefined);
      setDateValue("");
    } else {
      // If there is a defaultDate, sync with it
      const parsedDate = new Date(defaultDate);
      if (!isNaN(parsedDate.getTime())) {
        setDate(parsedDate);
        setDateValue(
          maskitoStringifyDate(parsedDate, {
            separator: "/",
            mode: "dd/mm/yyyy",
          }),
        );
      }
    }
  }, [defaultDate]);

  return (
    <div className="w-full relative">
      <Input
        ref={maskedInputRef}
        group={group}
        className={`${className} w-full`}
        value={dateValue}
        onBlur={() => {
          const parsedDate = maskitoParseDate(dateValue, params);
          if (!parsedDate && date) {
            setDateValue(
              maskitoStringifyDate(date, {
                separator: "/",
                mode: "dd/mm/yyyy",
              }),
            );
            return;
          }
        }}
        onChange={(e) => {
          const parsedDate = maskitoParseDate(e.target.value, params);

          if (!parsedDate && e.target.value.length === 0 && date) {
            setDateValue(
              maskitoStringifyDate(date, {
                separator: "/",
                mode: "dd/mm/yyyy",
              }),
            );
            return;
          }

          if (
            parsedDate &&
            parsedDate.getFullYear() >
              new Date().getFullYear() + MAX_YEAR_OFFSET
          ) {
            if (date) {
              setDateValue(
                maskitoStringifyDate(date, {
                  separator: "/",
                  mode: "dd/mm/yyyy",
                }),
              );
              return;
            }
            setDateValue("");
            return;
          }

          if (parsedDate) {
            setDate(parsedDate);
            onChangeDate(parsedDate.toISOString());
          }
          setDateValue(e.target.value);
        }}
        placeholder="Seleccionar fecha"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Seleccionar fecha"
            className="top-1.5 right-4 absolute"
          >
            <Icon type="far" name="calendar" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full overflow-hidden p-0 bg-neutral-object border-neutral"
          align="end"
        >
          <Calendar
            mode="single"
            selected={date}
            captionLayout="dropdown"
            defaultMonth={date || new Date()}
            endMonth={
              new Date(new Date().getFullYear() + MAX_YEAR_OFFSET, 11, 31)
            }
            disabled={
              shouldDisabledFutureDates ? { after: new Date() } : undefined
            }
            className="bg-transparent w-full max-w-full"
            onSelect={(date) => {
              setDate(date);
              onChangeDate(date?.toISOString() || "");
              setOpen(false);
              if (date) {
                setDateValue(
                  maskitoStringifyDate(date, {
                    separator: "/",
                    mode: "dd/mm/yyyy",
                  }),
                );
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ComparativeValueInputCalendar;
